import styled from 'styled-components';
import {getColorFromTheme} from '@ui/ui-utils';
import {Button} from '@ui/atoms/button';

export const FeedbackPageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
`;

export const FeedbackPageMain = styled.div`
  width: 100%;
  margin: 0 auto;
`;

export const FeedbackPageHeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const FeedbackPageHeading = styled.div`
  h1 {
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
  }

  p {
    max-width: 760px;
    margin: 4px 0 0;
    color: ${getColorFromTheme('textBody')};
    font-size: 16px;
    line-height: 24px;
  }
`;

export const FeedbackCreateButton = styled(Button)`
  display: inline-flex;
  width: 117px;
  height: 46px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px;

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }

  @media (max-width: 480px) {
    width: auto;
    padding: 12px 16px;
  }
`;

export const ActionMenu = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ActionMenuItem = styled.button`
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;
  transition: background-color 200ms ease, color 200ms ease;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('purple300')};
  }
`;

export const FeedbackSurface = styled.section`
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 12px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 8px 24px rgba(36, 36, 36, 0.04);
`;

export const FeedbackToolbar = styled.div`
  display: flex;
  min-height: 68px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
    padding: 16px;
  }
`;

export const FeedbackContent = styled.div`
  padding: 16px;

  @media (max-width: 640px) {
    padding: 12px;
  }
`;

export const FeedbackStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FeedbackSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 12px;

  h2 {
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }

  span {
    color: ${getColorFromTheme('textBody')};
    font-size: 14px;
    line-height: 22px;
  }
`;

export const FeedbackRequestsHeader = styled.div<{
  $tone?: 'pending' | 'neutral';
}>`
  display: flex;
  min-height: 24px;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;

  h2 {
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 16px/24px Graphik, sans-serif;
  }

  .feedback-requests-header-dot {
    width: 8px;
    height: 8px;
    flex: 0 0 8px;
    border-radius: 50%;
    background: ${getColorFromTheme('borderDark')};
  }

  .feedback-requests-header-status {
    color: ${({$tone}) =>
      $tone === 'neutral' ? getColorFromTheme('textBody') : '#e87f16'};
    font: 400 14px/22px Graphik, sans-serif;
  }

  .feedback-requests-header-count {
    display: inline-flex;
    min-width: 16px;
    height: 16px;
    align-items: center;
    justify-content: center;
    border-radius: 20px;
    background: ${({$tone}) =>
      $tone === 'neutral' ? getColorFromTheme('textBody') : '#e87f16'};
    padding: 0 4px;
    color: ${getColorFromTheme('alwaysWhite')};
    font: 500 12px/16px Graphik, sans-serif;
  }
`;

export const FeedbackReceivedHeader = styled.div`
  display: flex;
  min-height: 24px;
  align-items: center;
  gap: 24px;
  margin: 0 0 12px;

  h2 {
    flex: 0 0 auto;
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 16px/24px Graphik, sans-serif;
  }

  span {
    height: 1px;
    flex: 1 1 auto;
    background: ${getColorFromTheme('borderLight')};
  }
`;

export const FeedbackFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  .feedback-filter-group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;

    .feedback-filter-group {
      width: 100%;
    }
  }
`;

export const FeedbackFilterButton = styled.button`
  display: inline-flex;
  height: 40px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  padding: 7px 12px;
  color: ${getColorFromTheme('textBody')};
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;

  strong {
    color: ${getColorFromTheme('purple300')};
    font-weight: 600;
  }

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }

  &:hover,
  &[aria-expanded='true'] {
    border-color: ${getColorFromTheme('purple300')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${getColorFromTheme('textMuted')};

    strong {
      color: ${getColorFromTheme('textMuted')};
    }
  }
`;

export const FeedbackSelect = styled.select`
  min-width: 142px;
  height: 40px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  padding: 0 34px 0 12px;
  color: ${getColorFromTheme('textBody')};
  font: inherit;
  font-size: 14px;
  font-weight: 500;

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const FeedbackDateButton = styled(FeedbackFilterButton)`
  gap: 12px;
  color: ${getColorFromTheme('blackTwo')};
`;

export const FeedbackFilterDivider = styled.span`
  width: 1px;
  height: 24px;
  margin: 0 6px;
  background: ${getColorFromTheme('borderLight')};

  @media (max-width: 480px) {
    display: none;
  }
`;

export const FeedbackFilterMenu = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FeedbackFilterSearch = styled.label`
  display: flex;
  height: 36px;
  align-items: center;
  gap: 8px;
  margin: 2px 4px 6px;
  padding: 0 8px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 6px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};

  &:focus-within {
    border-color: ${getColorFromTheme('purple300')};
  }

  svg {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
  }

  input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: ${getColorFromTheme('textBody')};
    font: 400 13px/20px Graphik, sans-serif;

    &::placeholder {
      color: ${getColorFromTheme('textMuted')};
    }

    &::-webkit-search-cancel-button {
      display: none;
      appearance: none;
    }
  }

  button {
    display: grid;
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    place-items: center;
    border-radius: 4px;
    color: #5f5f8c;

    &:hover,
    &:focus-visible {
      outline: none;
      background: #f0f1ff;
      color: #585adf;
    }
  }
`;

export const FeedbackFilterOptions = styled.div`
  max-height: 266px;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

export const FeedbackFilterEmpty = styled.p`
  margin: 0;
  padding: 10px;
  color: ${getColorFromTheme('textMuted')};
  font: 400 13px/20px Graphik, sans-serif;
`;

export const FeedbackFilterOption = styled.button<{$active?: boolean}>`
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('backgroundDark') : getColorFromTheme('white')};
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;
  white-space: nowrap;
  transition: background-color 200ms ease, color 200ms ease;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('purple300')};
  }
`;

export const FeedbackDetailCard = styled.article`
  display: flex;
  flex-direction: column;
  overflow: visible;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};

  .feedback-detail-header,
  .feedback-detail-heading,
  .feedback-detail-visibility,
  .feedback-detail-byline {
    display: flex;
    align-items: center;
  }

  .feedback-detail-header {
    justify-content: space-between;
    gap: 16px;
    padding: 12px 16px;
    border-radius: 9px 9px 0 0;
    border-bottom: 1px solid ${getColorFromTheme('borderLight')};
    background: ${getColorFromTheme('white')};
  }

  .feedback-detail-heading {
    min-width: 0;
    gap: 8px;
  }

  .feedback-detail-heading h3 {
    overflow: hidden;
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 16px/24px Graphik, sans-serif;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .feedback-detail-heading--request h3,
  .feedback-detail-heading--feedback h3,
  .feedback-detail-heading--received h3,
  .feedback-detail-heading--praise h3 {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  .feedback-detail-heading--request {
    flex: 1 1 auto;

    h3 {
      overflow: visible;
      text-overflow: clip;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }

  .feedback-detail-kind,
  .feedback-detail-visibility {
    flex: 0 0 auto;
    border-radius: 8px;
    padding: 4px 8px;
    font: 600 12px/20px Graphik, sans-serif;
    white-space: nowrap;
  }

  .feedback-detail-kind {
    background: ${getColorFromTheme('green100')};
    color: ${getColorFromTheme('green300')};
  }

  .feedback-detail-kind--request {
    border-radius: 8px;
    background: ${getColorFromTheme('orange100')};
    padding: 4px 8px;
    color: #e87f16;
    font-weight: 500;
  }

  .feedback-detail-kind--sent-request {
    background: ${getColorFromTheme('backgroundDark')};
    padding: 4px 8px;
    color: ${getColorFromTheme('textBody')};
    font-weight: 500;
  }

  .feedback-detail-kind--praise {
    background: ${getColorFromTheme('purple100')};
    color: ${getColorFromTheme('purple300')};
    font-weight: 500;
  }

  .feedback-detail-visibility {
    gap: 4px;
    justify-content: center;
    background: ${getColorFromTheme('backgroundDark')};
    padding: 3px 7px;
    color: ${getColorFromTheme('textBody')};
    font-weight: 400;
    line-height: 18px;

    svg {
      width: 15px;
      height: 15px;
      flex: 0 0 15px;
    }
  }

  .feedback-detail-visibility--public {
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textBody')};
  }

  .feedback-detail-content {
    display: flex;
    flex-direction: column;
  }

  .feedback-request-actions,
  .feedback-request-meta,
  .feedback-request-meta-actions,
  .feedback-detail-meta {
    display: flex;
    align-items: center;
  }

  .feedback-request-actions {
    flex: 0 0 auto;
    gap: 8px;
  }

  .feedback-request-meta {
    justify-content: space-between;
    gap: 12px;
    margin: 10px 16px;

    .feedback-detail-byline {
      max-width: calc(100% - 112px);
      margin: 0;
    }
  }

  .feedback-request-meta-actions {
    flex: 0 0 auto;
    gap: 8px;
  }

  .feedback-detail-meta {
    justify-content: space-between;
    gap: 12px;
    margin: 10px 16px 0;

    .feedback-detail-byline {
      max-width: calc(100% - 112px);
      margin: 0;
    }
  }

  .feedback-detail-date {
    flex: 0 0 auto;
    color: ${getColorFromTheme('textBody')};
    font: 400 13px/20px Graphik, sans-serif;
    white-space: nowrap;
  }

  .feedback-request-dismiss {
    flex: 0 0 auto;
    border-radius: 6px;
    padding: 6px 8px;
    color: ${getColorFromTheme('textBody')};
    font: 500 13px/20px Graphik, sans-serif;

    &:hover,
    &:focus-visible {
      outline: none;
      background: ${getColorFromTheme('backgroundDark')};
      color: ${getColorFromTheme('purple300')};
    }
  }

  .feedback-detail-byline {
    width: fit-content;
    max-width: calc(100% - 32px);
    min-height: 36px;
    gap: 6px;
    margin: 10px 16px 0;
    padding: 4px 8px;
    border-radius: 8px;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('blackTwo')};

    > strong {
      min-width: 34px;
      font: 600 13px/20px Graphik, sans-serif;
    }
  }

  .feedback-detail-copy {
    min-height: 52px;
    padding: 12px 16px 14px;
    color: ${getColorFromTheme('blackTwo')};
    font: 400 14px/22px Graphik, sans-serif;

    .feedback-detail-copy-text,
    p,
    li {
      margin: 0;
      font: inherit;
    }

    .feedback-praise-recipient-names {
      margin-top: 10px;
      color: ${getColorFromTheme('textBody')};
      font: 500 13px/20px Graphik, sans-serif;
      overflow-wrap: anywhere;
    }
  }

  .feedback-praise-meta,
  .feedback-praise-people,
  .feedback-praise-person {
    display: flex;
    align-items: center;
  }

  .feedback-praise-meta {
    justify-content: space-between;
    gap: 16px;
    margin: 10px 16px 0;
  }

  .feedback-praise-people {
    width: fit-content;
    max-width: 100%;
    min-height: 40px;
    min-width: 0;
    gap: 10px;
    border-radius: 8px;
    background: ${getColorFromTheme('backgroundDark')};
    padding: 4px 10px 4px 6px;
  }

  .feedback-praise-person {
    min-width: 0;
    gap: 6px;

    > span {
      overflow: hidden;
      color: ${getColorFromTheme('blackTwo')};
      font: 500 13px/20px Graphik, sans-serif;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .feedback-praise-recipients {
    display: inline-flex;
    align-items: center;
    border-radius: 6px;
  }

  .feedback-praise-arrow {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
    color: ${getColorFromTheme('textBody')};
  }

  @media (max-width: 640px) {
    .feedback-detail-header {
      align-items: flex-start;
      flex-direction: column;
      gap: 10px;
      padding: 12px 14px;
    }

    .feedback-detail-heading {
      width: 100%;
    }

    .feedback-request-actions {
      width: 100%;
      justify-content: space-between;
    }

    .feedback-detail-byline {
      max-width: calc(100% - 28px);
      margin: 10px 14px 0;

      > strong {
        width: auto;
      }
    }

    .feedback-detail-copy {
      padding: 12px 14px 14px;
    }

    .feedback-praise-meta,
    .feedback-detail-meta,
    .feedback-request-meta {
      margin: 10px 14px 0;
    }

    .feedback-detail-meta,
    .feedback-request-meta {
      .feedback-detail-byline {
        max-width: calc(100% - 112px);
        margin: 0;
      }
    }

    .feedback-praise-people {
      width: 100%;
      flex-wrap: wrap;
    }

    .feedback-request-meta {
      margin-bottom: 12px;
    }
  }
`;

export const FeedbackCard = styled.article<{
  $tone?: 'feedback' | 'praise' | 'request';
}>`
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 12px;
  background: ${getColorFromTheme('white')};

  .feedback-card-main {
    padding: 20px 24px;
  }

  .feedback-card-top,
  .feedback-card-person,
  .feedback-card-meta,
  .feedback-card-footer,
  .feedback-card-actions {
    display: flex;
    align-items: center;
  }

  .feedback-card-top,
  .feedback-card-footer {
    justify-content: space-between;
    gap: 16px;
  }

  .feedback-card-person,
  .feedback-card-meta,
  .feedback-card-actions {
    gap: 8px;
  }

  .feedback-card-person {
    min-width: 0;
  }

  .feedback-card-person-copy {
    min-width: 0;
  }

  .feedback-card-person-copy strong,
  .feedback-card-person-copy span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .feedback-card-person-copy strong {
    color: ${getColorFromTheme('blackTwo')};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  .feedback-card-person-copy span,
  .feedback-card-date,
  .feedback-card-footer {
    color: ${getColorFromTheme('textBody')};
    font-size: 13px;
    line-height: 20px;
  }

  .feedback-card-subject {
    display: inline-flex;
    width: fit-content;
    margin-top: 18px;
    border-radius: 8px;
    background: ${({$tone}) =>
      $tone === 'praise'
        ? '#f3f0ff'
        : $tone === 'request'
        ? '#f6f6f8'
        : '#eef9f4'};
    padding: 6px 10px;
    color: ${({$tone}) =>
      $tone === 'praise'
        ? '#5850ec'
        : $tone === 'request'
        ? '#5f5f8c'
        : '#1f8f63'};
    font-size: 13px;
    font-weight: 600;
    line-height: 20px;
  }

  .feedback-card-copy {
    margin-top: 14px;
    color: ${getColorFromTheme('blackTwo')};
    font-size: 15px;
    line-height: 24px;
  }

  .feedback-card-footer {
    min-height: 52px;
    border-top: 1px solid ${getColorFromTheme('borderLight')};
    background: ${getColorFromTheme('backgroundLight')};
    padding: 10px 24px;
  }

  @media (max-width: 640px) {
    .feedback-card-main {
      padding: 16px;
    }

    .feedback-card-top,
    .feedback-card-footer {
      align-items: flex-start;
      flex-direction: column;
    }

    .feedback-card-footer {
      padding: 12px 16px;
    }
  }
`;

export const FeedbackChip = styled.span<{
  $tone?: 'neutral' | 'success' | 'purple';
}>`
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  background: ${({$tone}) =>
    $tone === 'success'
      ? '#eef9f4'
      : $tone === 'purple'
      ? '#f3f0ff'
      : '#f8f8fa'};
  padding: 4px 8px;
  color: ${({$tone}) =>
    $tone === 'success'
      ? '#1f8f63'
      : $tone === 'purple'
      ? '#5850ec'
      : '#68688f'};
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

export const FeedbackEmpty = styled.div`
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 12px;
  background: ${getColorFromTheme('white')};
  padding: 56px 24px;
  text-align: center;

  h2 {
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font-size: 18px;
    font-weight: 600;
    line-height: 26px;
  }

  p {
    margin: 6px 0 0;
    color: ${getColorFromTheme('textBody')};
    font-size: 14px;
    line-height: 22px;
  }
`;
